<template>
  <div class="fade-in overflow-hidden relative">
    <!-- Loading overlay -->
    <div
      v-if="expeditionsStore.isLoading"
      class="min-h-full absolute inset-0 bg-white bg-opacity-90 z-10"
    >
      <div class="flex items-center max-h-52 justify-center pt-52">
        <Loader2 class="w-4 h-4 mr-2 animate-spin" />
        <span class="text-sm text-gray-600 font-medium">Loading data...</span>
      </div>
    </div>

    <div class="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <!-- Loop through weeks -->
      <div v-for="(items, week) in processedData" :key="week">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-white">
            <tr>
              <th scope="col" class="w-12 py-3 text-center text-xs font-normal text-gray-500">#</th>
              <th scope="col" class="py-3 pr-2 text-xs font-normal text-gray-500 w-8 text-center">
                Section
              </th>
              <th scope="col" class="py-3 text-left text-xs font-normal text-gray-500">Title</th>
              <th scope="col" class="py-3 text-center text-xs font-normal text-gray-500 w-16">
                Points
              </th>
              <th scope="col" class="py-3 text-left text-xs font-normal text-gray-500 min-w-28">Status</th>
              <th scope="col" class="relative py-3 w-10">
                <span class="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <!-- Loop through items within a week -->
            <template v-for="(item, index) in items" :key="item.id">
              <tr @click="toggleExpand(item.id)" class="hover:bg-gray-50 cursor-pointer">
                <td class="py-4 text-center text-sm text-gray-400">{{ index + 1 }}</td>
                <td class="py-2 pr-2 whitespace-normal text-sm text-gray-400 text-center">
                  <div class="flex justify-center items-center">
                    <HoverCard
                      v-if="
                        item.path_step.task.section?.name &&
                        getSectionIcon(item.path_step.task.section?.name)
                      "
                    >
                      <HoverCardTrigger>
                        <component
                          :is="getSectionIcon(item.path_step.task.section?.name)"
                          class="w-5 h-5 text-gray-500 cursor-pointer"
                        />
                      </HoverCardTrigger>
                      <HoverCardContent class="w-auto p-2 text-sm">
                        {{ item.path_step.task.section?.name }}
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                </td>
                <td class="py-4 whitespace-normal text-sm text-gray-900">
                  <a
                    v-if="item.path_step.task.url && isUrl(item.path_step.task.url)"
                    :href="item.path_step.task.url"
                    target="_blank"
                    class="text-gray-900 hover:text-cosmicPurple inline-flex items-center transition-all duration-200"
                    @click.stop
                  >
                    <span>{{ item.path_step.task.name }}</span>
                    <ExternalLink class="w-3 h-3 ml-1 shrink-0 text-cosmicPurple" />
                    <span
                      v-if="!item.path_step.is_required"
                      class="text-xs bg-yellow-100 text-yellow-800 ml-2 px-2 py-0.5 rounded-full"
                      >Optional</span
                    >
                  </a>
                  <template v-else>
                    <span>{{ item.path_step.task.name }}</span>
                    <span
                      v-if="!item.path_step.is_required"
                      class="text-xs bg-yellow-100 text-yellow-800 ml-2 px-2 py-0.5 rounded-full"
                      >Optional</span
                    >
                  </template>
                </td>
                <td
                  class="py-4 whitespace-normal text-sm text-center flex items-center justify-center"
                >
                  <span
                    v-if="item.path_step.points > 0"
                    class="text-cosmicPurple bg-cosmicPurple/20 px-2 py-1 rounded-full min-w-7 min-h-7 text-center"
                    >{{ item.path_step.points }}</span
                  >
                </td>
                <td class="py-4 whitespace-normal text-sm">
                  <span
                    :class="getStatusClass(item.status)"
                    class="px-2 py-1 text-xs rounded-full font-medium"
                  >
                    {{ formatStatus(item.status) }}
                  </span>
                </td>
                <td class="py-4 text-sm text-gray-500">
                  <div class="text-gray-400">
                    <ChevronDown v-if="!expandedItems.has(item.id)" class="w-5 h-5" />
                    <ChevronUp v-else class="w-5 h-5" />
                  </div>
                </td>
              </tr>
              <tr v-if="expandedItems.has(item.id)">
                <td colspan="6" class="bg-lightGray p-2">
                  <div
                    class="flex space-x-4 max-w-[1000px] mx-auto gap-4 border-[1px] border-gray-200 rounded-lg p-8 bg-white"
                  >
                    <!-- Left Column: Info and Notes -->
                    <div class="flex-1 space-y-4">
                      <!-- Task Info -->
                      <div class="space-y-1 flex flex-col gap-2">
                        <span class="flex items-center mb-2">
                          <CircleCheckBig
                            v-if="item.status === 'DONE'"
                            class="w-5 h-5 mr-2 mt-0.5 text-lime-800"
                          />
                          <h2 class="text-gray-900 font-semibold">
                            {{ item.path_step.task.name }}
                          </h2>
                          <span
                            v-if="item.path_step.is_required"
                            class="text-xs bg-cosmicPurple/20 text-cosmicPurple ml-2 px-2 py-0.5 rounded-full"
                            >Required</span
                          >
                          <span
                            v-if="!item.path_step.is_required"
                            class="text-xs bg-yellow-100 text-yellow-800 ml-2 px-2 py-0.5 rounded-full"
                            >Optional</span
                          >
                        </span>
                        <p
                          v-if="item.path_step.task.section?.name"
                          class="text-sm flex items-start"
                        >
                          <span class="flex items-center min-w-32">
                            <TableOfContents class="w-4 h-4 mr-2 rotate-180" />
                            <span class="text-gray-700 mr-2 font-semibold">Section:</span>
                          </span>
                          <span class="text-gray-900">{{ item.path_step.task.section?.name }}</span>
                        </p>
                        <p v-if="item.path_step.points > 0" class="text-sm flex items-start">
                          <span class="flex items-center min-w-32">
                            <Info class="w-4 h-4 mr-2" />
                            <span class="text-gray-700 mr-2 font-semibold">Points:</span>
                          </span>
                          <span class="text-gray-900 flex items-center">
                            <img src="/ot_coin.svg" class="w-4 h-4 mr-1" />
                            {{ item.path_step.points }}
                          </span>
                        </p>
                        <p v-if="item.path_step.task.details" class="text-sm flex items-start">
                          <span class="flex items-center min-w-32">
                            <Info class="w-4 h-4 mr-2" />
                            <span class="text-gray-700 mr-2 font-semibold">Details:</span>
                          </span>
                          <span class="block text-gray-900">
                            {{ item.path_step.task.details }}
                          </span>
                        </p>
                        <p
                          v-if="item.path_step.task.url && !isUrl(item.path_step.task.url)"
                          class="text-sm flex items-start ml-32 text-gray-900"
                        >
                          {{ item.path_step.task.url }}
                        </p>
                      </div>
                    </div>

                    <!-- Right Column: Buttons -->
                    <div class="w-48 flex-shrink-0 flex flex-col space-y-2">
                      <div
                        v-if="item.status === 'DONE'"
                        class="w-full border-lime-800 text-lime-800 border-[1px] px-4 py-2 rounded-md flex items-center justify-center text-sm"
                      >
                        <CheckCircle class="w-4 h-4 mr-2" />
                        Completed
                      </div>
                      <div
                        v-else-if="item.status === 'IN-PROGRESS'"
                        class="w-full border-cosmicPurple/10 text-cosmicPurple border-[1px] px-4 py-2 rounded-md flex items-center justify-center text-sm"
                      >
                        <LoaderCircle class="w-4 h-4 mr-2" />
                        In Progress
                      </div>
                      <div
                        v-else-if="item.status === 'TO START'"
                        class="w-full border-gray-500 text-gray-500 border-[1px] px-4 py-2 rounded-md flex items-center justify-center text-sm"
                      >
                        Not Started
                      </div>
                      <a
                        v-if="item.path_step.task.url && isUrl(item.path_step.task.url)"
                        :href="item.path_step.task.url"
                        target="_blank"
                        class="w-full bg-cosmicPurple text-white px-4 py-2 rounded-md flex items-center justify-center text-sm hover:-translate-y-[1px] transition-transform duration-200 hover:shadow-md"
                      >
                        <ExternalLink class="w-4 h-4 mr-2" />
                        Open Task
                      </a>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits } from 'vue'
import {
  ChevronDown,
  ChevronUp,
  Loader2,
  ExternalLink,
  CircleCheckBig,
  CheckCircle,
  LoaderCircle,
  TableOfContents,
  Info
} from 'lucide-vue-next'
import type { PersonalNote, TableProgressItem } from '@/types/curriculum'
import { isUrl } from '@/utils/isUrl'
import { useExpeditionsStore } from '@/stores/expeditions'
import { getSectionIcon } from '@/const/curriculumSections'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { groupAndSortByWeek } from '@/utils/curriculumSorting'

// Remove the local interface definition since we're importing it
const props = defineProps<{
  items: TableProgressItem[]
  isLoading: boolean
  isNotesLoading: boolean
  isUpdating: boolean
  personalNotes: Record<number, PersonalNote | null>
}>()

// Adjust Emits - remove save-notes, update-status, notes-interacted
const emit = defineEmits<{
  (e: 'toggle-expand', id: number): void
}>()

const expeditionsStore = useExpeditionsStore()

// Change from single value ref to Set
const expandedItems = ref<Set<number>>(new Set())

// Computed property to structure the data by weeks
const processedData = computed(() => {
  return groupAndSortByWeek(props.items || [])
})

// Update toggle logic to add/remove IDs from the Set
const toggleExpand = async (id: number) => {
  if (expandedItems.value.has(id)) {
    expandedItems.value.delete(id)
  } else {
    expandedItems.value.add(id)
    // Emit event for parent to handle fetching (if still needed for notes)
    emit('toggle-expand', id)
  }
}

// --- Keep display logic functions ---
const getStatusClass = (status: string) => {
  switch (status) {
    case 'DONE':
      return 'bg-lime-100 text-lime-800'
    case 'IN-PROGRESS':
      return 'bg-cosmicPurple/10 text-cosmicPurple'
    case 'TO START':
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatStatus = (status: string) => {
  switch (status) {
    case 'TO START':
      return 'Not Started'
    case 'IN-PROGRESS':
      return 'In Progress'
    case 'DONE':
      return 'Completed'
    default:
      return status
  }
}
</script>
